import { apiGet, apiRequest } from "@/lib/api/client";
import type { Position } from "@/types";

// Formato bruto devolvido pelo Traccar em GET /api/positions — convertido
// para o tipo Position da aplicação em toPosition() antes de sair deste serviço.
// Exportado porque o WebSocket de tempo real (use-realtime-sync.ts) manda
// posições no MESMO formato bruto (documentado no protocolo do Traccar) e
// precisa da mesma conversão pra aplicar o patch incremental no cache.
export interface TraccarPosition {
  id: number;
  deviceId: number;
  latitude: number;
  longitude: number;
  speed: number; // nós (padrão do Traccar)
  course: number;
  fixTime: string;
  valid: boolean;
  protocol?: string;
  attributes?: {
    ignition?: boolean;
    motion?: boolean;
    blocked?: boolean;
    batteryLevel?: number; // % da bateria interna do rastreador
    power?: number; // tensão da alimentação externa (bateria do veículo), volts
    battery?: number; // tensão da bateria interna/backup, volts
    // Vários protocolos "baratos" (ex: gt06) não normalizam a leitura de
    // tensão em "power" — mandam a entrada analógica crua em adc1/adc2.
    // Nesses casos adc1 é, na prática, a tensão de alimentação do veículo.
    adc1?: number;
    adc2?: number;
    hours?: number; // horímetro, ms
    odometer?: number; // odômetro real (OBD), metros — nem todo protocolo reporta
    totalDistance?: number; // distância acumulada calculada pelo Traccar via GPS, metros — NÃO é o odômetro real
    alarm?: string; // ex: "lowBattery", "tamper", "powerCut", "powerRestored"
    charge?: boolean; // gt06: alimentação externa carregando (true = energia ligada)
    [key: string]: unknown;
  };
}

const KNOTS_TO_KMH = 1.852;

function toPosition(p: TraccarPosition): Position {
  return {
    id: String(p.id),
    vehicleId: String(p.deviceId),
    lat: p.latitude,
    lng: p.longitude,
    speed: Math.round(p.speed * KNOTS_TO_KMH),
    bearing: p.course,
    ignition: p.attributes?.ignition,
    motion: p.attributes?.motion,
    blocked: p.attributes?.blocked,
    valid: p.valid,
    batteryLevel: p.attributes?.batteryLevel,
    power: p.attributes?.power ?? p.attributes?.adc1,
    battery: p.attributes?.battery,
    hours: p.attributes?.hours,
    odometer: p.attributes?.odometer,
    totalDistance: p.attributes?.totalDistance,
    alarm: p.attributes?.alarm,
    charge: p.attributes?.charge,
    timestamp: p.fixTime,
    protocol: p.protocol,
  };
}

// GET /api/positions do Traccar (org.traccar.api.resource.PositionResource,
// confirmado lendo os @QueryParam da classe compilada) só aceita
// deviceId/id/geofenceId/from/to — não existe parâmetro `limit` no servidor.
// A única forma de limitar o volume de resposta é encurtar a janela from/to.
const GPS_FIX_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;
// blocked/ignition explícitos aparecem a cada poucos pacotes (ver comentário
// em vehicles.service.ts sobre o intercalamento de tipos de pacote gt06) —
// 2h de janela é suficiente pra achar um valor explícito na frota atual e
// mantém a resposta pequena (centenas de posições, não dezenas de milhares).
const STICKY_SEED_LOOKBACK_MS = 2 * 60 * 60 * 1000;
// Nenhuma consulta de histórico pode ficar pendurada indefinidamente — acima
// disso, aborta e trata como "não encontrado" (nunca deve derrubar quem
// chamou, ver .catch abaixo).
export const HISTORY_REQUEST_TIMEOUT_MS = 8_000;

// Base para os dois fallbacks de histórico abaixo (coordenada válida e
// último blocked/ignition confirmado) — mesmo endpoint real do Traccar,
// janela e timeout configuráveis por chamada.
async function fetchHistory(vehicleId: string, lookbackMs: number): Promise<TraccarPosition[]> {
  const to = new Date();
  const from = new Date(to.getTime() - lookbackMs);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HISTORY_REQUEST_TIMEOUT_MS);
  try {
    return await apiRequest<TraccarPosition[]>(
      `/api/positions?deviceId=${vehicleId}&from=${from.toISOString()}&to=${to.toISOString()}`,
      { method: "GET", credentials: "include", fallback: [], signal: controller.signal },
    );
  } catch {
    // Timeout (AbortError) ou qualquer outro erro de rede/HTTP — histórico é
    // sempre auxiliar, nunca pode propagar falha pra quem chamou.
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
}

// Busca o histórico recente de um dispositivo e devolve o fix GPS válido
// mais recente nele — usado quando a "última posição" reportada pelo
// Traccar não tem coordenadas (ex: keep-alive sem sinal de GPS). Janela mais
// longa que a do seed sticky abaixo de propósito: o rastreador pode ter
// ficado dias sem sinal de GPS válido.
async function lastValidFromHistory(vehicleId: string): Promise<Position | undefined> {
  const raw = await fetchHistory(vehicleId, GPS_FIX_LOOKBACK_MS);
  const valid = raw.filter((p) => p.valid);
  if (!valid.length) return undefined;
  valid.sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
  return toPosition(valid[valid.length - 1]);
}

export interface LastKnownAttributes {
  blocked?: boolean;
  ignition?: boolean;
}

// Vários protocolos (ex.: gt06) só incluem `blocked`/`ignition` em
// determinados tipos de pacote — a posição mais recente de um device pode
// perfeitamente não trazer nenhum dos dois, mesmo que o rastreador já os
// tenha reportado minutos antes. Usado só uma vez por veículo, no cold
// start (ver vehicles.service.ts `seedStickyIfNeeded`), para semear o
// último valor EXPLÍCITO já confirmado — nunca chamado a cada poll.
async function lastKnownAttributes(vehicleId: string): Promise<LastKnownAttributes> {
  const raw = await fetchHistory(vehicleId, STICKY_SEED_LOOKBACK_MS);
  raw.sort((a, b) => new Date(b.fixTime).getTime() - new Date(a.fixTime).getTime());
  const result: LastKnownAttributes = {};
  for (const p of raw) {
    if (result.blocked === undefined && p.attributes?.blocked !== undefined) {
      result.blocked = p.attributes.blocked;
    }
    if (result.ignition === undefined && p.attributes?.ignition !== undefined) {
      result.ignition = p.attributes.ignition;
    }
    if (result.blocked !== undefined && result.ignition !== undefined) break;
  }
  return result;
}

export const positionsService = {
  // Exportado pro patch incremental do WebSocket (use-realtime-sync.ts) —
  // mesma conversão bruto->Position usada aqui, nunca duplicada.
  toPosition,
  latest: async () => {
    const raw = await apiRequest<TraccarPosition[]>("/api/positions", {
      method: "GET",
      credentials: "include",
      fallback: [],
    });
    const positions = raw.map(toPosition);
    // O relatório mais recente pode não ter fix de GPS (comum em
    // keep-alives). Nesses casos, mantém os dados dos sensores (velocidade,
    // atributos) do relatório mais novo, mas usa a última coordenada válida
    // conhecida para não perder o veículo do mapa.
    return Promise.all(
      positions.map(async (p) => {
        if (p.valid !== false) return p;
        const lastValid = await lastValidFromHistory(p.vehicleId);
        if (!lastValid) return p;
        return { ...p, lat: lastValid.lat, lng: lastValid.lng, bearing: lastValid.bearing };
      }),
    );
  },
  byVehicle: (vehicleId: string, params?: { from?: string; to?: string }) => {
    const qs = params
      ? "?" +
        new URLSearchParams(
          Object.entries(params).filter(([, v]) => v) as [string, string][],
        ).toString()
      : "";
    return apiGet<Position[]>(`/vehicles/${vehicleId}/positions${qs}`, []);
  },

  lastKnownAttributes,

  // Busca posições específicas por id (GET /api/positions?id=...&id=...) —
  // usado para conferir se o evento deviceMoving/deviceStopped que o
  // Traccar gerou realmente bate com o motion/velocidade da posição que ele
  // mesmo referenciou (ver useVehicleMotionSince), sem precisar baixar o
  // histórico de rota inteiro.
  byIds: async (ids: number[]): Promise<Map<number, { motion?: boolean; speed: number }>> => {
    const map = new Map<number, { motion?: boolean; speed: number }>();
    if (ids.length === 0) return map;
    const params = new URLSearchParams();
    for (const id of ids) params.append("id", String(id));
    const raw = await apiRequest<TraccarPosition[]>(`/api/positions?${params.toString()}`, {
      method: "GET",
      credentials: "include",
      fallback: [],
    }).catch(() => [] as TraccarPosition[]);
    for (const p of raw) {
      map.set(p.id, { motion: p.attributes?.motion, speed: Math.round(p.speed * KNOTS_TO_KMH) });
    }
    return map;
  },
};
